import React, { useEffect, useState } from "react";
import * as attachmentService from "../../../models/services/attachmentService";
import * as taskService from "../../../models/services/taskService";
import { ISessionState } from "../../../models/session/sessionState";
import { ListDetailsPanel } from "source/components/framework/List/ListDetailsPanel";
import { AppUtils } from "source/framework/appUtils";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskEditItemTaskState } from "source/models/task/taskEditItemTaskState";
import { ITaskEditItemTaskPriority } from "source/models/task/taskEditItemTaskPriority";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ITaskEditItem } from "source/models/task/taskEditItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { Alert, Grid } from "@mui/material";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { ModifyUserInfo } from "source/components/framework/User/ModifyUserInfo";
import { ListDetailsPanelAccordion } from "source/components/framework/List/ListDetailsPanel/ListDetailsPanelAccordion";
import { TaskDynamicFormContainer } from "../TaskDynamicFormContainer";
import { TaskDynamicForm } from "../TaskDynamicForm/TaskDynamicForm";
import { DatabaseRecordType, TaskHandlerType, TaskRepetitionType, TaskStateEditabilityType } from "ui/Scripts/source/models/enums";
import { AttachmentList } from "source/components/attachments/AttachmentList/AttachmentList";
import downloadjs from "downloadjs";
import { TaskDataRowList } from "../TaskDataRowList";
import { UserBlocks } from "source/components/user/UserBlocks/UserBlocks";
import { FormFieldLockedToUser } from "source/components/framework/FormFieldLockedToUser";
import { CalendarTaskListOfRecord } from "source/components/calendarTask/CalendarTaskListOfRecord";
import { ITaskHandler } from "source/models/task/taskHandler";
import { TaskStateListChip } from "../TaskStateListChip";
import { ListStateIconArchived } from "source/components/framework/List/ListStateIconArchived/ListStateIconArchived";

interface ITaskListDetailsPanelProps {
    sessionState: ISessionState;
    companyId: string;
    taskId: string;
    dataId: string;
    editDialogVisible: boolean;
    filterTaskTypeFields: string;
    onClose: () => void;
}

interface ITaskListDetailsPanelState {
    task: ITaskEditItem;
    taskType: ITaskEditTypeItem;
    taskState: ITaskEditItemTaskState;
    taskPriority: ITaskEditItemTaskPriority;
    unit: IIdTitleNode;
    handlers: ITaskHandler[];
    personsInCharge: ITaskHandler[];
    isLocked: boolean;
    isLockedToOtherUser: boolean;
    canUpdateTask: boolean;
}

export const TaskListDetailsPanel = (props: ITaskListDetailsPanelProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingDataRows, setIsLoadingDataRows] = useState(false);
    const [isLoadingCalendarTaskList, setIsLoadingCalendarTaskList] = useState(false);
    const [state, setState] = useState<ITaskListDetailsPanelState>({
        task: null,
        taskType: null,
        taskState: null,
        taskPriority: null,
        unit: null,
        handlers: [],
        personsInCharge: [],
        isLocked: false,
        isLockedToOtherUser: false,
        canUpdateTask: false
    });

    const changeState = (obj: any) => {
        setState(prevState => ({
            ...prevState,
            ...obj
        }));
    };

    const loadTask = async () => {
        if (!props.taskId) return;
        setIsLoading(true);
        const result = await AppUtils.callService(() => taskService.getTaskEdit(props.companyId, props.taskId, false), false, true, () => setIsLoading(false));
        if (!result) return;
        const task = result.item;
        const taskType = result.taskTypes.find(i => i.id === task.taskTypeId);
        const taskState = result.taskStates.find(i => i.id === task.taskStateId);
        changeState({
            task,
            taskType,
            taskState,
            taskPriority: result.taskPriorities.find(i => i.id === task.taskPriorityId),
            unit: task.unitId ? IdTitleNode.getById(result.units, task.unitId) : null,
            handlers: task.handlers.filter(i => i.taskHandlerType === TaskHandlerType.Handler),
            personsInCharge: task.handlers.filter(i => i.taskHandlerType === TaskHandlerType.PersonInCharge),
            isLocked: taskState?.editabilityType === TaskStateEditabilityType.ReadOnly,
            isLockedToOtherUser: task.isLockedToOtherUser(props.sessionState.code),
            canUpdateTask: result.canUpdateTask
        });
    };

    useEffect(() => {
        if (props.editDialogVisible) return;
        loadTask();
    }, [props.companyId, props.taskId, props.dataId, props.filterTaskTypeFields, props.editDialogVisible]);

    const handleAttachmentDownload = async (id: string) => {
        if (!id) return;
        const attachment = state.task.taskDataRows[0].attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    const isArchived = !!state.task?.archiveUser;

    return (
        <ListDetailsPanel
            isLoading={isLoading}
            onClose={props.onClose}
        >
            {(!props.taskId || (!state.task && isLoading)) &&
                <Alert severity="info">{Translations.SelectTaskInListToSeeThePreview}</Alert>
            }
            {!!state.task &&
                <>
                    <div style={{ padding: "8px 16px 16px" }}>
                        <GridRow>
                            <Grid item sm={6}>
                                <FormField title={Translations.TaskCreated} fullWidth margin={0}>
                                    <ModifyUserInfo
                                        modifyUser={state.task.createUser}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={6}>
                                <FormField title={Translations.Modified} fullWidth margin={0}>
                                    <ModifyUserInfo
                                        modifyUser={state.task.modifyUser}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        {state.isLockedToOtherUser &&
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormFieldLockedToUser
                                        lockedUser={state.task.lockedUser}
                                    />
                                </Grid>
                            </GridRow>
                        }
                        {state.task.archiveUser &&
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.Archived} fullWidth>
                                        <ModifyUserInfo
                                            modifyUser={state.task.archiveUser}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        {state.task.deleteUser &&
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.MovedToTrashBin} fullWidth>
                                        <ModifyUserInfo
                                            modifyUser={state.task.deleteUser}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                    </div>
                    <ListDetailsPanelAccordion
                        title={Translations.Task}
                    >
                        <>
                            <GridRow>
                                <Grid item sm={3}>
                                    <FormField title={Translations.TaskType} margin={0}>
                                        {state.taskType.code}
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.TaskState} margin={0}>
                                        <TaskStateListChip
                                            icon={isArchived ? <ListStateIconArchived hideTooltip={true} /> : undefined}
                                            code={isArchived ? Translations.Archived : state.taskState?.code}
                                            color={isArchived ? "EBEBEB" : state.taskState?.color}
                                            fullWidth={true}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.Priority} margin={0}>
                                        {state.taskPriority.code}
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.Code} margin={0}>
                                        {state.task.code}
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={state.unit ? Translations.Unit : Translations.Visibility}>
                                        {state.unit ? state.unit.title : Translations.Private}
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.Title}>
                                        {state.task.name}
                                    </FormField>
                                </Grid>
                            </GridRow>
                            {!!state.unit &&
                                <GridRow>
                                    <Grid item sm={12}>
                                        <UserBlocks
                                            avatarOnly={true}
                                            companyId={props.companyId}
                                            isReadOnly={true}
                                            title={Translations.PersonsInCharge}
                                            users={state.personsInCharge}
                                            onAddUsers={null}
                                            onRemoveUsers={null}
                                            onGetUserSelectListItems={null}
                                        />
                                        <UserBlocks
                                            avatarOnly={true}
                                            companyId={props.companyId}
                                            isReadOnly={true}
                                            title={Translations.TaskHandlers}
                                            noUsersMessage={Translations.AllUsersOfTheUnit}
                                            noUsersMessageUseInReadOnly={true}
                                            fixedUsers={state.handlers.length > 0 && state.task.createUser ? [state.task.createUser] : undefined}
                                            users={state.handlers}
                                            onAddUsers={null}
                                            onRemoveUsers={null}
                                            onGetUserSelectListItems={null}
                                        />
                                    </Grid>
                                </GridRow>
                            }
                            {state.task.taskHeaderDataCols.length > 0 &&
                                <div style={{ paddingTop: "1.5rem" }}>
                                    <TaskDynamicFormContainer>
                                        <TaskDynamicForm
                                            isReadOnly={true}
                                            taskTypeCols={state.taskType.headerCols}
                                            taskDataRowCols={state.task.taskHeaderDataCols}
                                            onRefreshCols={null}
                                        />
                                    </TaskDynamicFormContainer>
                                </div>
                            }
                        </>
                    </ListDetailsPanelAccordion>
                    {state.taskType.taskRepetitionType === TaskRepetitionType.NotRepeated && state.task.taskDataRows.length > 0 &&
                        <ListDetailsPanelAccordion
                            title={Translations.Contents}
                        >
                            <TaskDynamicFormContainer>
                                <TaskDynamicForm
                                    isReadOnly={true}
                                    taskTypeCols={state.taskType.formCols}
                                    taskDataRowCols={state.task.taskDataRows[0].cols}
                                    onRefreshCols={null}
                                />
                            </TaskDynamicFormContainer>
                        </ListDetailsPanelAccordion>
                    }
                    {state.taskType.taskRepetitionType === TaskRepetitionType.Repeated &&
                        <ListDetailsPanelAccordion
                            isLoading={isLoadingDataRows}
                            title={Translations.TaskDataRows}
                        >
                            <TaskDataRowList
                                sessionState={props.sessionState}
                                isReadOnly={state.isLocked || !state.canUpdateTask || isArchived}
                                companyId={props.companyId}
                                taskId={state.task.id}
                                dataId={props.dataId}
                                taskType={state.taskType}
                                filterFields={props.filterTaskTypeFields}
                                detailsViewMode={true}
                                onIsLoadingChange={setIsLoadingDataRows}
                            />
                        </ListDetailsPanelAccordion>
                    }
                    <ListDetailsPanelAccordion
                        isLoading={isLoadingCalendarTaskList}
                        title={Translations.CalendarTasks}
                    >
                        <CalendarTaskListOfRecord
                            sessionState={props.sessionState}
                            isReadOnly={state.isLocked || !state.canUpdateTask || isArchived}
                            infoText={state.task.unitId ? Translations.TaskCalendarTasksInfo : undefined}
                            warningText={!state.task.unitId || state.personsInCharge.length > 0 ? undefined : Translations.TaskCalendarTasksPersonsInChargeWarning}
                            companyId={props.companyId}
                            recordId={state.task.id}
                            ownerRecordTitle={Translations.Task + ": " + state.task.name}
                            databaseRecordType={DatabaseRecordType.Task}
                            noDataMessage={Translations.TaskMustBeSavedBeforeCalendarTasksCanBeAdded}
                            dataId={props.dataId}
                            detailsViewMode={true}
                            onIsLoadingChange={setIsLoadingCalendarTaskList}
                        />
                    </ListDetailsPanelAccordion>
                    {state.taskType.taskRepetitionType === TaskRepetitionType.NotRepeated && state.task.taskDataRows.length > 0 && state.task.taskDataRows[0].attachments.length > 0 &&
                        <ListDetailsPanelAccordion
                            title={Translations.Attachments}
                        >
                            <AttachmentList
                                isReadOnly={true}
                                title={null}
                                attachments={state.task.taskDataRows[0].attachments}
                                margin="0.5rem 0 0 0"
                                onRemoveAttachment={null}
                                onDownloadAttachment={handleAttachmentDownload}
                                onAddAttachments={null}
                            />
                        </ListDetailsPanelAccordion>
                    }
                </>
            }
        </ListDetailsPanel>
    );
};
